import { useConfirm } from 'components/modals/message/MessageProvider';
import { Blocker, useBlocker } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { FEATURE_TOGGLE_FORM_CONFIRMATION_BEFORE_LEAVE } from 'env';

interface DirtyFormState {
  isDirty: () => boolean;
}

/**
 * Handles the scenario where the user tries to navigate away from a form with unsaved changes,
 * prompting them with a confirmation dialog.
 *
 * @param {DirtyFormState} form - The active form state with potential unsaved changes.
 * @param {boolean} enabled - Whether this form enables the guard independently of the feature toggle.
 * @return {Blocker} The blocker instance that manages the navigation blocking behavior.
 */
export function useDirtyFormHandler(
  form: DirtyFormState,
  enabled = FEATURE_TOGGLE_FORM_CONFIRMATION_BEFORE_LEAVE
): Blocker {
  const { confirm } = useConfirm();
  const blocker = useBlocker(() => enabled && form.isDirty());

  const confirmBeforeFormLeft = useCallback(() => {
    confirm({
      title: 'Potvrdenie pokračovania',
      content: 'Vo formulári sú neuložené zmeny. Naozaj chcete pokračovať?',
      onConfirm: () => {
        blocker.proceed?.();
      },
    });
  }, [confirm, blocker]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      confirmBeforeFormLeft();
    }
  }, [blocker.state, confirmBeforeFormLeft]);

  return blocker;
}
